import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  Link as MuiLink,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Typography,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router-dom';
import remarkGfm from 'remark-gfm';

import logo from '../assets/field-ops-logo.png';
import { CLIENT_BRAND_NAME } from '../branding';
import { PageHeader } from '../dashboard/PageHeader';
import type { LegalDocument } from './legalDocuments';

export type LegalDocumentPageProps = {
  document: LegalDocument;
  presentation: LegalDocumentPresentation;
};

export type LegalDocumentPresentation = 'public' | 'dashboard';

const markdownComponents = {
  h1: ({ children }: { children?: React.ReactNode }) => (
    <Typography component="h2" variant="h5" sx={{ mt: 5, mb: 2, fontWeight: 700 }}>
      {children}
    </Typography>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <Typography component="h3" variant="h6" sx={{ mt: 4, mb: 1.5, fontWeight: 700 }}>
      {children}
    </Typography>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <Typography component="h4" variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 700 }}>
      {children}
    </Typography>
  ),
  p: ({ children }: { children?: React.ReactNode }) => (
    <Typography component="p" variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
      {children}
    </Typography>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <Box component="ul" sx={{ mt: -0.5, mb: 2, pl: 3.5 }}>
      {children}
    </Box>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <Box component="ol" sx={{ mt: -0.5, mb: 2, pl: 3.5 }}>
      {children}
    </Box>
  ),
  li: ({ children }: { children?: React.ReactNode }) => (
    <Typography component="li" variant="body1" sx={{ pl: 0.5, mb: 0.75, lineHeight: 1.7 }}>
      {children}
    </Typography>
  ),
  a: ({ children, href }: { children?: React.ReactNode; href?: string }) => (
    <MuiLink href={href} underline="hover">
      {children}
    </MuiLink>
  ),
  table: ({ children }: { children?: React.ReactNode }) => (
    <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, maxWidth: '100%', overflowX: 'auto' }}>
      <Table size="small">{children}</Table>
    </TableContainer>
  ),
  thead: ({ children }: { children?: React.ReactNode }) => <TableHead>{children}</TableHead>,
  tbody: ({ children }: { children?: React.ReactNode }) => <TableBody>{children}</TableBody>,
  tr: ({ children }: { children?: React.ReactNode }) => <TableRow>{children}</TableRow>,
  th: ({ children }: { children?: React.ReactNode }) => (
    <TableCell component="th" scope="col" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
      {children}
    </TableCell>
  ),
  td: ({ children }: { children?: React.ReactNode }) => <TableCell sx={{ verticalAlign: 'top' }}>{children}</TableCell>,
};

type LegalDocumentContentProps = Pick<LegalDocumentPageProps, 'document'> & {
  showTitle?: boolean;
};

const LegalDocumentContent = ({ document, showTitle = true }: LegalDocumentContentProps) => (
  <Paper variant="outlined" sx={{ p: { xs: 2, sm: 4 } }}>
    {showTitle && (
      <>
        <Typography component="h1" variant="h3" sx={{ fontWeight: 700 }}>
          {document.label}
        </Typography>
        <Divider sx={{ my: 3 }} />
      </>
    )}
    <Box>
      <ReactMarkdown remarkPlugins={[remarkGfm]} skipHtml components={markdownComponents}>
        {document.source}
      </ReactMarkdown>
    </Box>
  </Paper>
);

export const LegalDocumentPage = ({ document, presentation }: LegalDocumentPageProps) => {
  if (presentation === 'dashboard') {
    return (
      <>
        <PageHeader
          title={document.label}
          action={
            <Button component={Link} to="/" color="inherit" startIcon={<ArrowBackIcon />}>
              Return to dashboard
            </Button>
          }
        />
        <Container component="div" maxWidth="md" disableGutters>
          <LegalDocumentContent document={document} showTitle={false} />
        </Container>
      </>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        component="header"
        position="static"
        color="default"
        elevation={0}
        sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}
      >
        <Toolbar disableGutters sx={{ minHeight: 72, px: { xs: 2, sm: 3 } }}>
          <Box component="img" src={logo} alt={CLIENT_BRAND_NAME} sx={{ height: 56, width: 'auto' }} />
          <Box sx={{ flexGrow: 1 }} />
          <Button component={Link} to="/" color="inherit" startIcon={<ArrowBackIcon />}>
            Return to sign in
          </Button>
        </Toolbar>
      </AppBar>
      <Container component="main" maxWidth="md" sx={{ py: { xs: 3, sm: 6 } }}>
        <LegalDocumentContent document={document} />
      </Container>
    </Box>
  );
};
